import { WebflowStyleGuide } from 'base/copy_settings';
import { conceptDomain, designDomain } from 'base/styleguide/model';
import { localeSchema } from 'base/ui/locales';
import { randomUUID } from 'base/uuid';
import { AtomDomain } from 'blocks/woot_woot/atom';
import { ConfigOf, MutableOf, StateOf } from 'blocks/woot_woot/base';
import { ListDomain } from 'blocks/woot_woot/list';
import { withMigration } from 'blocks/woot_woot/migration';
import { OptionalDomain } from 'blocks/woot_woot/optional';
import { RecordDomain } from 'blocks/woot_woot/record';
import { RecordUnionDomain } from 'blocks/woot_woot/record_union';
import {
  createStringMapStateSchema,
  recordMap,
  StringMapDomain,
} from 'blocks/woot_woot/string_map';
import { z } from 'zod';

/**
 * The backend & frontend check that they are using the same schema version.  This only needs
 * to be incremented when a breaking change is made to the model.  When considering if a change
 * is breaking, consider not just the state schema but also the DELTAS.  E.g. we changed from
 * using `Section::component` to `Section::element` and even though we could map the states
 * (the `withMigration` code) the deltas would not be compatible.
 *
 * If you are just adding a new field with a default, that would not be a breaking change.
 *
 * The process for incrementing MODEL_SCHEMA_VERSION involves downtime:
 *
 * 1. Shut down the backend (or at least the websocket part of the backend)
 * 2. Run the "end_all_project_sessions" command on the backend to flush any in-flight data
 * 3. Make the code change, increment this number on both the backend and frontend
 * 4. Re-deploy and re-start the backend, Re-deploy the frontend
 **/
export const MODEL_SCHEMA_VERSION = 2;

const commentAnchoringViewportSchema = z.enum([
  'sitemap',
  'wireframe-desktop',
  'wireframe-tablet',
  'wireframe-mobile',
  'styleguide',
]);
export type CommentAnchoringViewport = z.infer<
  typeof commentAnchoringViewportSchema
>;
const baseCommentAnchoring = {
  viewport: new AtomDomain(commentAnchoringViewportSchema),
  /* Ranging from 0 (left) to 1 (right) */
  x: new AtomDomain(z.number()),
  /* Ranging from 0 (top) to 1 (bottom) */
  y: new AtomDomain(z.number()),
} as const;
const commentAnchoringDomain = new RecordDomain(baseCommentAnchoring);
export type CommentAnchoring = MutableOf<
  ConfigOf<typeof commentAnchoringDomain>
>;
export type CommentAnchoringState = StateOf<
  ConfigOf<typeof commentAnchoringDomain>
>;
const optionalNodeIdCommentAnchoringDomain = new RecordDomain({
  ...baseCommentAnchoring,
  nodeId: new AtomDomain(z.string().optional()),
});
export type OptionalNodeIdCommentAnchoringState = StateOf<
  ConfigOf<typeof optionalNodeIdCommentAnchoringDomain>
>;

export const elementSourceSchema = z.object({
  /**
   * The slug of the component's file, i.e.
   *   https://components-public.relume.io/scrambled/{slug}.json
   **/
  slug: z.string(),
  /**
   * If omitted, the root node of the component file is used.
   * Otherwise, the node with a data-blocks-name attribute matching this value is used.
   **/
  rootName: z.string().optional(),
});
export type ElementSourceState = Readonly<z.infer<typeof elementSourceSchema>>;

export const MARGIN_SIZES = [
  'tiny',
  'xxsmall',
  'xsmall',
  'small',
  'medium',
  'large',
  'xlarge',
  'xxlarge',
  'huge',
  'xhuge',
  'xxhuge',
  // do we support custom1, custom2, custom3?
] as const;
export const MARGIN_DIRECTIONS = [
  'top',
  'right',
  'bottom',
  'left',
  'vertical',
  'horizontal',
] as const;

const marginStateSchema = z.object({
  size: z.enum(MARGIN_SIZES),
  direction: z.enum(MARGIN_DIRECTIONS),
});
export type MarginState = Readonly<z.infer<typeof marginStateSchema>>;

// Since ElementInstance is recursive, we need to define it in 2 steps:
const simpleElementInstanceFields = {
  id: new AtomDomain(z.string().optional()),
  source: new AtomDomain(elementSourceSchema),
  textOverrides: new StringMapDomain(new AtomDomain(z.string())),
  commentThreads: new StringMapDomain(optionalNodeIdCommentAnchoringDomain),
  margin: new AtomDomain(marginStateSchema.optional()),
} as const;
type ElementInstanceFields = typeof simpleElementInstanceFields & {
  slotOverrides: StringMapDomain<
    ConfigOf<
      RecordDomain<{
        items: ListDomain<ConfigOf<RecordDomain<ElementInstanceFields>>>;
      }>
    >
  >;
};
export const elementInstanceDomain: RecordDomain<ElementInstanceFields> =
  new RecordDomain({
    ...simpleElementInstanceFields,
    slotOverrides: new StringMapDomain(
      new RecordDomain({
        items: new ListDomain(() => elementInstanceDomain),
      }),
    ),
  });
export type ElementInstance = MutableOf<ConfigOf<typeof elementInstanceDomain>>;
export type SlotOverride = Exclude<
  ReturnType<ElementInstance['slotOverrides']['get']>,
  undefined
>;
export type ElementInstanceState = StateOf<
  ConfigOf<typeof elementInstanceDomain>
>;
export type SlotOverrideState = Exclude<
  ReturnType<ElementInstanceState['slotOverrides']['get']>,
  undefined
>;

export const SECTION_COLORS = [
  'grey',
  'brown',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
  'red',
] as const;
export type SectionColor = (typeof SECTION_COLORS)[number];

const sectionDomainWithoutMigration = new RecordDomain({
  name: new AtomDomain(z.string()),
  description: new AtomDomain(z.string()),
  color: new AtomDomain(z.enum(SECTION_COLORS).optional()),
  element: new OptionalDomain(elementInstanceDomain),
  /**
   * This flag should be set if the user edits name or description
   * while a component is selected & they can't see the component.
   **/
  isComponentOutOfSync: new AtomDomain(z.boolean().optional()),
});
export const sectionDomain = withMigration(
  sectionDomainWithoutMigration,
  // The old section schema, when we used "component" instead of "element"
  z
    .object({
      name: z.string(),
      description: z.string(),
      color: z.enum(SECTION_COLORS).optional(),
      isComponentOutOfSync: z.boolean().optional(),
      element: z.never().optional(),
      component: z.object({
        current: z
          .object({
            slug: z.string(),
            textChanges: z.union([
              createStringMapStateSchema(z.string()),
              // The old format was like: [{ id: '123', newText: 'hello world' }]
              z
                .object({
                  id: z.string(),
                  newText: z.string(),
                })
                .array()
                .transform((arr) => {
                  const map = new Map<string, string>();
                  for (const { id, newText } of arr) {
                    // previously, the first value won
                    if (map.has(id)) continue;
                    map.set(id, newText);
                  }
                  return recordMap(map);
                }),
            ]),
            commentThreads: createStringMapStateSchema(
              optionalNodeIdCommentAnchoringDomain.stateSchema,
            ),
          })
          .optional(),
      }),
    })
    .transform(
      (obj): StateOf<ConfigOf<typeof sectionDomainWithoutMigration>> => ({
        name: obj.name,
        description: obj.description,
        color: obj.color,
        isComponentOutOfSync: obj.isComponentOutOfSync,
        element: {
          current: obj.component.current
            ? {
                id: undefined,
                source: {
                  slug: obj.component.current.slug,
                },
                textOverrides: obj.component.current.textChanges,
                commentThreads: obj.component.current.commentThreads,
                margin: undefined,
                slotOverrides: recordMap(),
              }
            : undefined,
        },
      }),
    ),
  (latestSchema) =>
    latestSchema.and(z.object({ component: z.null().optional() })),
);

export type SectionState = StateOf<ConfigOf<typeof sectionDomain>>;
export type Section = MutableOf<ConfigOf<typeof sectionDomain>>;

export const sectionRefDomain = new RecordUnionDomain({
  inline: {
    value: sectionDomain,
    commentThreads: new StringMapDomain(commentAnchoringDomain),
  },
  reference: {
    id: new AtomDomain(z.string()),
    commentThreads: new StringMapDomain(commentAnchoringDomain),
  },
});
// SectionRefs are either an inline section, or a global section.
export type SectionRefState = StateOf<ConfigOf<typeof sectionRefDomain>>;
export type SectionRef = MutableOf<ConfigOf<typeof sectionRefDomain>>;

// If a page is a path, it has no content of it's own.
const pageTypeSchema = z.enum(['page', 'path']);

export type PageType = z.infer<typeof pageTypeSchema>;
// Since page is recursive, we need to define it in two steps:
type PageDomainFields = {
  name: AtomDomain<string>;
  description: AtomDomain<string>;
  sections: ListDomain<ConfigOf<typeof sectionRefDomain>>;
  subPages: ListDomain<ConfigOf<RecordDomain<PageDomainFields>>>;
  commentThreads: StringMapDomain<ConfigOf<typeof commentAnchoringDomain>>;
  pageType: AtomDomain<PageType | undefined>;
};

export const pageDomain: RecordDomain<PageDomainFields> = new RecordDomain({
  name: new AtomDomain(z.string()),
  description: new AtomDomain(z.string().default('') as z.Schema<string>),
  sections: new ListDomain(
    withMigration(
      // previously it was a list of sectionDomains, so we migrate those
      // into inline sectionRefs
      sectionRefDomain,
      z
        .object({ type: z.undefined().optional() })
        .and(sectionDomain.stateSchema)
        .transform((obj) => {
          return anInlineSection(obj) satisfies SectionRefState;
        }),
    ),
  ),
  subPages: new ListDomain(() => pageDomain),
  commentThreads: new StringMapDomain(commentAnchoringDomain),
  isPath: new AtomDomain(z.boolean().optional()),
  pageType: new AtomDomain(pageTypeSchema.optional()),
});
export type PageState = StateOf<ConfigOf<typeof pageDomain>>;
export type Page = MutableOf<ConfigOf<typeof pageDomain>>;

const preferredGenPageSchema = z.union([
  z.literal(4),
  z.literal(5),
  z.literal(10),
  z.literal(15),
  z.literal(20),
]);
export type PreferredGenPageNumbers = z.infer<typeof preferredGenPageSchema>;

export const spaceDomain = new RecordDomain({
  name: new AtomDomain(z.string()),
  description: new AtomDomain(z.string().default('')),
  localeOverride: new AtomDomain(localeSchema.optional()),
  // Intentionally not named "pages", so that `parent: Page | Space` is an
  // easy to use type.
  subPages: new ListDomain(pageDomain),
  commentThreads: new StringMapDomain(commentAnchoringDomain),
  preferredNumPages: new AtomDomain(preferredGenPageSchema.optional()),
});
export type SpaceState = StateOf<ConfigOf<typeof spaceDomain>>;
export type Space = MutableOf<ConfigOf<typeof spaceDomain>>;

const imagePropertiesDomain = new RecordDomain({
  fit: new AtomDomain(
    z
      .union([
        z.literal('cover'),
        z.literal('contain'),
        z.literal('none'),
        z.literal('fill'),
      ])
      .optional(),
  ),
  position: new AtomDomain(z.string().optional()),
  ratio: new AtomDomain(z.string().optional()),
  crop: new AtomDomain(
    z.tuple([z.number(), z.number(), z.number(), z.number()]),
  ),
});

export const imageRefDomain = new RecordUnionDomain({
  private: {
    id: new AtomDomain(z.number()),
    properties: imagePropertiesDomain,
  },
  unsplash: {
    base: new AtomDomain(z.string()),
    // TODO: zain, is this real or requested.
    width: new AtomDomain(z.number()),
    height: new AtomDomain(z.number()),
    properties: imagePropertiesDomain,
  },
  placeholder: {
    id: new AtomDomain(z.string()),
  },
});

export type ImageRefState = StateOf<ConfigOf<typeof imageRefDomain>>;
export type ImageRef = MutableOf<ConfigOf<typeof imageRefDomain>>;

/**
 * Effectively the data type of a Project. Edits will automatically be synced
 * with the backend and any other UI on page that needs to know.
 */
export const projectDomain = new RecordDomain({
  name: new AtomDomain(z.string().optional()),
  brief: new AtomDomain(z.string().optional()),
  locale: withMigration(
    new AtomDomain(localeSchema),
    localeSchema.default('en-US'),
  ),
  preferredNumPages: new AtomDomain(preferredGenPageSchema.optional()),
  homeSpaceName: new AtomDomain(z.string().optional()),
  homePage: pageDomain,
  spaces: new ListDomain(spaceDomain),
  globalSections: new StringMapDomain(sectionDomain),
  commentThreads: new StringMapDomain(commentAnchoringDomain),
  logo: new OptionalDomain(
    new RecordDomain({
      light: new OptionalDomain(imageRefDomain),
      dark: new OptionalDomain(imageRefDomain),
      height: new AtomDomain(z.number().default(32)),
    }),
  ),
  concepts: new ListDomain(conceptDomain),
  webflowStyle: new AtomDomain(z.nativeEnum(WebflowStyleGuide).optional()),
  design: new OptionalDomain(designDomain),
});
export type ProjectState = StateOf<ConfigOf<typeof projectDomain>>;
export type Project = MutableOf<ConfigOf<typeof projectDomain>>;

export function anElementInstance(
  part: Partial<ElementInstanceState> & Pick<ElementInstanceState, 'source'>,
): ElementInstanceState {
  return {
    id: randomUUID(),
    slotOverrides: recordMap(),
    textOverrides: recordMap(),
    commentThreads: recordMap(),
    margin: undefined,
    ...part,
  };
}

export function aSection(part?: Partial<SectionState>): SectionState {
  return {
    name: '',
    description: '',
    color: undefined,
    element: {
      current: undefined,
    },
    isComponentOutOfSync: false,
    ...part,
  };
}

export function anInlineSection(part?: Partial<SectionState>): SectionRefState {
  return {
    type: 'inline',
    value: aSection(part),
    commentThreads: recordMap(),
  };
}

export function aReferenceSection(part: { id: string }): SectionRefState {
  return {
    type: 'reference',
    commentThreads: recordMap(),
    ...part,
  };
}

export function aPage(part?: Partial<PageState>): PageState {
  return {
    name: '',
    description: '',
    sections: [],
    subPages: [],
    commentThreads: recordMap(),
    pageType: 'page',
    ...part,
  };
}

export function aSpace(part?: Partial<SpaceState>): SpaceState {
  return {
    name: '',
    description: '',
    localeOverride: undefined,
    subPages: [],
    commentThreads: recordMap(),
    preferredNumPages: 4,
    ...part,
  };
}

export function aProject(part?: Partial<ProjectState>): ProjectState {
  return {
    name: undefined,
    brief: '',
    locale: 'en-US',
    preferredNumPages: 4,
    homeSpaceName: undefined,
    homePage: aPage({ name: 'Home' }),
    spaces: [],
    globalSections: recordMap(),
    commentThreads: recordMap(),
    logo: { current: undefined },
    concepts: [],
    webflowStyle: WebflowStyleGuide.RelumeV3_CF2_1,
    design: { current: undefined },
    ...part,
  };
}

export function aPrivateImageRef(id: number): ImageRefState {
  return {
    type: 'private',
    id,
    properties: {
      fit: 'cover',
      position: undefined,
      ratio: undefined,
      crop: [0, 0, 0, 0],
    },
  };
}

export const MISSING_GLOBAL_SECTION_STATE = aSection({
  name: 'Missing global section',
  description: 'The global section referenced is missing',
  color: 'red',
});
